import { Box, Button, Link, styled, Typography } from "@mui/material";
import { rem } from "polished";
import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import appConfig from "config/app";
import { ariaLabel, PATH_ACCESSIBILITY, PATH_MULTI_ANNUAL_PLAN } from "src/constants";
import { SITE_MAP_PAGE } from "src/constants/app/pages";
import { BrandCodeEnum } from "src/graphql/generated/types";
import { useDeviceWidth } from "src/hooks";
import { useBrand } from "src/hooks/useBrand";
import { useBrandStore } from "src/stores/brand-store";
import { nonNullable } from "src/utils";

// Dimension are in pixels but will be converted in rem
export const footerHeight = 110;

const { enableThemeSwitcher } = appConfig.brand;
const hasEnableThemeSwitcher = enableThemeSwitcher === "true";

const brandCodes = Object.values(BrandCodeEnum);

interface Props {
  inverted?: boolean;
}
const StyledFooter = styled("footer")<Props>`
  background: ${(props) =>
    props.inverted ? props.theme.palette.common.white : props.theme.palette.primary.main};
  width: 100vw;
`;

const StyledContent = styled("div")<Props>`
  display: flex;
  margin-left: ${(props) => props.theme.spacing(8)};
  margin-right: ${(props) => props.theme.spacing(8)};
  height: ${rem(footerHeight)};
  align-items: center;
  justify-content: space-between;
  color: ${(props) =>
    props.inverted ? props.theme.palette.primary.main : props.theme.palette.primary.contrastText};

  ${(props) => props.theme.breakpoints.down("lg")} {
    justify-content: space-around;
    margin-left: 0;
    margin-right: 0;
    height: auto;
  }
`;

const LinkList = styled("div")`
  display: flex;

  & > div {
    margin-right: ${(props) => props.theme.spacing(1)};
  }

  ${(props) => props.theme.breakpoints.down("lg")} {
    flex-direction: column;
    padding: ${(props) => props.theme.spacing(1)};
    gap: ${(props) => props.theme.spacing(1)};
    & > a {
      margin-top: ${(props) => props.theme.spacing(1)};
    }
  }
`;

const Copyright = styled("div")`
  ${(props) => props.theme.breakpoints.down("lg")} {
    display: flex;
    flex-direction: column;
    & > * {
      margin-top: ${(props) => props.theme.spacing(1)};
    }
  }
`;

const VericalDivider = styled("div")<Props>`
  width: ${rem(1)};
  height: ${rem(70)};
  ${(props) => props.theme.breakpoints.down("sm")} {
    height: ${rem(140)};
  }
  background-color: ${(props) =>
    props.inverted ? props.theme.palette.primary.main : props.theme.palette.common.white};
`;

export const Footer = ({ inverted = false }: Props) => {
  const brandCode = useBrandStore((state) => state.brandCode);
  const setBrandCode = useBrandStore((state) => state.setBrandCode);
  const { isTabletMobile } = useDeviceWidth();
  const { dataProtectionPolicy, identity, customerPortal, code } = useBrand();

  const showAccessibility = code === BrandCodeEnum.Foncia;
  const { uuid } = useParams();

  const { origin } = window.location;
  const accessibilityUrl = `${origin}${PATH_ACCESSIBILITY}`;
  const multiAnnualPlanUrl = `${origin}${PATH_MULTI_ANNUAL_PLAN}`;

  const links = [
    {
      href: customerPortal.links.legalNoticeUrl,
      label: "Mentions légales",
    },
    {
      href: dataProtectionPolicy.url,
      label: "Charte RGPD",
    },
    uuid
      ? {
          to: SITE_MAP_PAGE,
          label: "Plan du site",
        }
      : undefined,
    {
      href: customerPortal.links.securityInformationUrl,
      label: "Sécurité informatique",
    },
    showAccessibility
      ? {
          href: accessibilityUrl,
          label: "Déclaration d'accessibilité",
        }
      : undefined,
    {
      href: multiAnnualPlanUrl,
      label: "Plan pluriannuel",
    },
    customerPortal.links.servicesUrl
      ? {
          href: customerPortal.links.servicesUrl,
          label: "Services",
        }
      : undefined,
  ].filter(nonNullable);

  return (
    <StyledFooter inverted={inverted} aria-label={ariaLabel.footer} role="contentinfo">
      <StyledContent inverted={inverted}>
        <Copyright>
          {isTabletMobile && (
            <img
              alt={identity.name}
              width="120"
              src={inverted ? identity.secondaryLogo.colorUrl : identity.secondaryLogo.whiteUrl}
            />
          )}
          <Box sx={{ m: 1, display: "flex", gap: 1, alignItems: "center" }}>
            <Typography>{`© ${new Date().getFullYear()} ${identity.name}`}</Typography>
            {hasEnableThemeSwitcher && (
              <>
                |
                {brandCodes
                  .filter((code) => code !== brandCode)
                  .map((code, index) => (
                    <React.Fragment key={code}>
                      {index > 0 && " | "}
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => setBrandCode(code)}
                        sx={{
                          textDecoration: "underline",
                          minWidth: 0,
                          p: 0,
                          fontWeight: "normal",
                        }}
                      >
                        {code}
                      </Button>
                    </React.Fragment>
                  ))}
              </>
            )}
          </Box>
        </Copyright>

        {isTabletMobile && <VericalDivider inverted={inverted} />}

        <LinkList>
          {links.map((link, index) => (
            <div key={`link-${index}`}>
              <Link
                href={link.href}
                component={link.to ? RouterLink : "a"}
                to={link.to}
                color="inherit"
                target={link.to ? undefined : "_blank"}
                rel={link.to ? undefined : "noopener"}
              >
                {link.label}
              </Link>
              {isTabletMobile === false && index < links.length - 1 && " | "}
            </div>
          ))}
        </LinkList>

        {!isTabletMobile && (
          <img
            alt={identity.name}
            width={120}
            src={inverted ? identity.secondaryLogo.colorUrl : identity.secondaryLogo.whiteUrl}
          />
        )}
      </StyledContent>
    </StyledFooter>
  );
};
